import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Btn } from '../Components/Btn';
import dfstyles from '../Styles/dfstyles';
import { GameWindowZIndex } from '../Utils/constants';
import {
  getGameTimeMs,
  getPlaybackSpeed,
  isMovingForward,
  isPaused,
  setPlaybackSpeed,
  setPaused,
  togglePlaybackDirection,
} from '../Utils/TimeUtils';

const StyledTimeControlPane = styled.div<{ collapsed: boolean }>`
  z-index: ${GameWindowZIndex.MenuBar};
  position: absolute;
  top: 0;
  right: 0;
  padding: ${({ collapsed }) => (collapsed ? '0.25em 0.5em' : '0.75em')};
  margin: 0.5em;
  background: ${({ collapsed }) =>
    collapsed ? 'rgba(0, 0, 0, 0.5)' : dfstyles.colors.background};
  border: ${({ collapsed }) =>
    collapsed ? '1px solid rgba(255, 255, 255, 0.2)' : `1px solid ${dfstyles.colors.border}`};
  border-radius: ${dfstyles.borderRadius};
  display: flex;
  flex-direction: column;
  gap: ${({ collapsed }) => (collapsed ? '0' : '0.5em')};
  min-width: ${({ collapsed }) => (collapsed ? 'auto' : '280px')};
  font-size: 0.9em;
  color: ${dfstyles.colors.text};
  cursor: ${({ collapsed }) => (collapsed ? 'pointer' : 'default')};
  transition: all 0.2s ease;
  backdrop-filter: ${({ collapsed }) => (collapsed ? 'blur(4px)' : 'none')};

  &:hover {
    background: ${({ collapsed }) =>
      collapsed ? 'rgba(0, 0, 0, 0.7)' : dfstyles.colors.background};
  }
`;

const TimeDisplay = styled.div<{ collapsed: boolean }>`
  font-family: monospace;
  font-size: ${({ collapsed }) => (collapsed ? '0.75em' : '1.1em')};
  padding: ${({ collapsed }) => (collapsed ? '0.25em 0.5em' : '0.5em')};
  background: ${({ collapsed }) =>
    collapsed ? 'transparent' : dfstyles.colors.backgroundlighter};
  border-radius: ${dfstyles.borderRadius};
  text-align: center;
  cursor: pointer;
  user-select: none;
  
  &:hover {
    opacity: ${({ collapsed }) => (collapsed ? '0.8' : '1')};
  }
`;

const SpeedControl = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25em;
`;

const SpeedLabel = styled.label`
  color: ${dfstyles.colors.subtext};
  font-size: 0.85em;
`;

const SpeedInput = styled.input`
  background: ${dfstyles.colors.backgroundlighter};
  border: 1px solid ${dfstyles.colors.border};
  border-radius: ${dfstyles.borderRadius};
  padding: 0.5em;
  color: ${dfstyles.colors.text};
  font-size: 1em;
  width: 100%;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.5em;
  justify-content: space-between;
`;

const ControlButton = styled(Btn)`
  flex: 1;
  padding: 0.5em;
  font-size: 0.9em;
`;

function formatDateTime(timestamp: number, collapsed: boolean = false): string {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(Math.floor(date.getMilliseconds() / 100)).padStart(1, '0');
  
  if (collapsed) {
    // More compact format for collapsed state
    return `${month}/${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
  }
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
}

export function TimeControlPane() {
  const [currentTime, setCurrentTime] = useState<number>(getGameTimeMs());
  const [speed, setSpeed] = useState<number>(Math.abs(getPlaybackSpeed()));
  const [isMovingFwd, setIsMovingFwd] = useState<boolean>(isMovingForward());
  const [paused, setPausedState] = useState<boolean>(isPaused());
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true); // Start collapsed

  // Linear time update using requestAnimationFrame for smooth display
  useEffect(() => {
    let animationFrameId: number;
    let lastSyncTime = performance.now();

    const updateTime = () => {
      // Always get the current time directly from TimeUtils which handles speed calculations
      const gameTime = getGameTimeMs();
      setCurrentTime(gameTime);
      
      const now = performance.now();
      
      // Update other states less frequently (every 100ms) to reduce overhead
      const timeSinceSync = now - lastSyncTime;
      if (timeSinceSync > 100) {
        setIsMovingFwd(isMovingForward());
        setPausedState(isPaused());
        const currentSpeed = getPlaybackSpeed();
        setSpeed(Math.abs(currentSpeed));
        lastSyncTime = now;
      }
      
      animationFrameId = requestAnimationFrame(updateTime);
    };

    animationFrameId = requestAnimationFrame(updateTime);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSpeed = parseFloat(e.target.value);
    if (!isNaN(newSpeed) && newSpeed >= 5) {
      const finalSpeed = isMovingFwd ? newSpeed : -newSpeed;
      setSpeed(newSpeed);
      setPlaybackSpeed(finalSpeed);
    } else if (!isNaN(newSpeed) && newSpeed < 5) {
      // Auto-adjust to minimum speed
      const finalSpeed = isMovingFwd ? 5.0 : -5.0;
      setSpeed(5.0);
      setPlaybackSpeed(finalSpeed);
    }
  };

  const handleDirectionToggle = () => {
    togglePlaybackDirection();
    const newFwd = isMovingForward();
    setIsMovingFwd(newFwd);
    setSpeed(Math.abs(getPlaybackSpeed()));
  };

  const handlePauseToggle = () => {
    const newPaused = !paused;
    setPaused(newPaused);
    setPausedState(newPaused);
  };

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <StyledTimeControlPane collapsed={isCollapsed}>
      <TimeDisplay collapsed={isCollapsed} onClick={handleToggleCollapse}>
        {formatDateTime(currentTime, isCollapsed)}
      </TimeDisplay>

      {!isCollapsed && (
        <>
          <SpeedControl>
            <SpeedLabel>Playback Speed (minimum 5x)</SpeedLabel>
            <SpeedInput
              type="number"
              min="5"
              step="1"
              value={Math.abs(speed)}
              onChange={handleSpeedChange}
            />
          </SpeedControl>

          <ButtonRow>
            <ControlButton onClick={handleDirectionToggle}>
              {isMovingFwd ? '▶ Forward' : '◀ Reverse'}
            </ControlButton>
            <ControlButton onClick={handlePauseToggle}>
              {paused ? '▶ Play' : '⏸ Pause'}
            </ControlButton>
          </ButtonRow>
        </>
      )}
    </StyledTimeControlPane>
  );
}

